import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { RefreshCw, Tv, X } from 'lucide-react';
import type {
  ProtoHubLoadState,
  ProtoHubSource,
  ProtoHubTarget,
} from '@volley/crucible-ipc-types/ipc-channels';

export interface ProtoHubSimulatorSurfaceProps {
  target: ProtoHubTarget;
  source: ProtoHubSource;
  gameId?: string;
  onClose: () => void;
}

/**
 * Creates a renderer-local id that correlates one open request with its close.
 *
 * Args:
 *   None.
 *
 * Returns:
 *   Unique-enough request id for cancelling an in-flight ProtoHub open.
 *
 * Raises:
 *   Never.
 */
function createProtoHubRequestId(): string {
  const uuid = globalThis.crypto?.randomUUID?.();
  if (uuid) return uuid;
  return `protohub-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

export function ProtoHubSimulatorSurface({
  target,
  source,
  gameId,
  onClose,
}: ProtoHubSimulatorSurfaceProps): React.JSX.Element {
  const screenRef = useRef<HTMLDivElement>(null);
  const activeRequestIdRef = useRef<string | null>(null);
  const [loadState, setLoadState] = useState<ProtoHubLoadState>('loading');
  const [error, setError] = useState<string | null>(null);
  const [closing, setClosing] = useState(false);

  useEffect(() => {
    const unsub = window.crucible.on('protohub:load-state', (payload) => {
      setLoadState(payload.state);
      setError(payload.error ?? null);
    });
    return () => unsub();
  }, []);

  useEffect(() => {
    let cancelled = false;
    const requestId = createProtoHubRequestId();
    activeRequestIdRef.current = requestId;
    setLoadState('loading');
    setError(null);
    void window.crucible
      .invoke('protohub:open', {
        target,
        source,
        requestId,
        ...(gameId ? { gameId } : {}),
      })
      .then((result) => {
        if (!cancelled && !result.ok) {
          setLoadState('failed');
          setError(result.error ?? 'Failed to open ProtoHub');
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setLoadState('failed');
          setError(err instanceof Error ? err.message : String(err));
        }
      });

    return () => {
      cancelled = true;
      if (activeRequestIdRef.current !== requestId) return;
      activeRequestIdRef.current = null;
      void window.crucible.invoke('protohub:close', { requestId });
    };
  }, [gameId, source, target]);

  useLayoutEffect(() => {
    const screen = screenRef.current;
    if (!screen) return;

    let frame = 0;
    const reportBounds = (): void => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        const rect = screen.getBoundingClientRect();
        void window.crucible.invoke('protohub:set-bounds', {
          x: rect.x,
          y: rect.y,
          width: rect.width,
          height: rect.height,
        });
      });
    };

    reportBounds();
    const observer =
      typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(reportBounds);
    observer?.observe(screen);
    window.addEventListener('resize', reportBounds);

    return () => {
      cancelAnimationFrame(frame);
      observer?.disconnect();
      window.removeEventListener('resize', reportBounds);
    };
  }, []);

  const reload = (): void => {
    setLoadState('loading');
    setError(null);
    void window.crucible.invoke('protohub:reload');
  };

  const close = (): void => {
    const requestId = activeRequestIdRef.current;
    activeRequestIdRef.current = null;
    setClosing(true);
    void window.crucible.invoke('protohub:close', requestId ? { requestId } : {}).finally(onClose);
  };

  return (
    <section className="flex h-full min-h-0 w-full flex-col bg-[#111317] text-white">
      <div className="flex h-12 shrink-0 items-center justify-between border-b border-white/[0.08] px-4">
        <div className="flex min-w-0 items-center gap-2 text-[13px] font-medium text-white/75">
          <Tv size={16} className="text-cyan-200/80" />
          <span className="truncate">ProtoHub</span>
        </div>
        <div className="flex items-center gap-1.5">
          <button
            type="button"
            aria-label="Reload ProtoHub"
            title="Reload ProtoHub"
            onClick={reload}
            className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-white/[0.1] bg-white/[0.04] text-white/65 transition-colors hover:border-white/[0.2] hover:bg-white/[0.08] hover:text-white"
          >
            <RefreshCw size={15} className={loadState === 'loading' ? 'animate-spin' : ''} />
          </button>
          <button
            type="button"
            aria-label="Close ProtoHub"
            title="Close ProtoHub"
            onClick={close}
            disabled={closing}
            className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-white/[0.1] bg-white/[0.04] text-white/65 transition-colors hover:border-white/[0.2] hover:bg-white/[0.08] hover:text-white disabled:cursor-not-allowed disabled:opacity-50"
          >
            <X size={15} />
          </button>
        </div>
      </div>
      <div className="flex min-h-0 flex-1 items-center justify-center px-5 py-5">
        <div className="relative aspect-video max-h-full w-full max-w-[1200px] rounded-[28px] bg-[#050607] p-3 shadow-[0_30px_80px_rgba(0,0,0,0.45)] ring-1 ring-white/[0.12]">
          <div
            ref={screenRef}
            className="relative h-full w-full overflow-hidden rounded-[18px] bg-black ring-1 ring-white/[0.08]"
          >
            {loadState !== 'ready' && (
              <div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center bg-black/55">
                <div className="max-w-[min(80%,420px)] text-center text-[13px] text-white/72">
                  {loadState === 'failed'
                    ? (error ?? 'ProtoHub failed to load')
                    : 'Loading ProtoHub'}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
